import L from "@/modules/login/Login.module.css";

import { apiBackends } from "@/apiBackends.ts";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import * as mswInit from "@/mock-api/msw/mswInit.ts";
import { apikeyAtom } from "@/modules/api/apikey.store.ts";
import { apiPrefixAtom } from "@/modules/api/createApiClient.ts";
import { lastApiKeyAtom } from "@/modules/login/ApiKeyLogin.store.ts";
import { InputRow } from "@/modules/login/InputRow.tsx";
import { useLingui } from "@lingui/react/macro";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { useAtomValue, useSetAtom, useStore } from "jotai/react";
import { useEffect } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";

interface Inputs {
    apikey: string;
    enableMockApi: boolean;
}

const defaultMockApiKey = "mock-key";

export function ApiKeyLoginForm() {
    //#region Hooks
    const { t } = useLingui();
    const setApikey = useSetAtom(apikeyAtom);

    const store = useStore();
    const apiPrefix = useAtomValue(apiPrefixAtom);
    const lastApiKey =
        getOwn(store.get(lastApiKeyAtom), apiPrefix) ??
        apiBackends.find((backend) => backend.apiPrefix === apiPrefix)?.defaultApiKey ??
        "";

    const form = useForm<Inputs>({
        defaultValues: {
            enableMockApi: mswInit.isMockApiEnabled(),
            apikey: lastApiKey,
        },
    });

    // Update the form field value when the selected API key changes
    useEffect(() => {
        form.setValue("apikey", lastApiKey, { shouldTouch: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastApiKey]);

    //#endregion

    const { register, watch } = form;

    const onSubmit: SubmitHandler<Inputs> = (data) => {
        const { enableMockApi, apikey } = data;

        const enteredApiKey = apikey.trim();
        const finalApikey = enableMockApi ? defaultMockApiKey : enteredApiKey;

        const mswChanged = mswInit.isMockApiEnabled() !== enableMockApi;

        // NB: must come first so no requests are made before MSW is initialized
        mswInit.setEnabled(enableMockApi);

        setApikey(finalApikey);

        store.set(lastApiKeyAtom, (x) => ({ ...x, [apiPrefix]: enteredApiKey }));

        if (mswChanged) {
            // Reload the page to clear various caches after MSW is enabled/disabled
            window.location.reload();
        }
    };

    const mockApiRow = mswInit.isSupported() ? (
        <div className="pb-2">
            <input id="enableMockApi" type="checkbox" {...register("enableMockApi")} />{" "}
            <label htmlFor="enableMockApi">Enable mock API</label>
        </div>
    ) : null;

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            <InputRow
                name="apikey"
                label={
                    "API key" // no-translate
                }
                autoComplete="on"
                disabled={watch("enableMockApi")}
                isRequired={!watch("enableMockApi")}
                autoFocus
            />

            {mockApiRow}

            <FormSubmitButton
                label={t`Log in`}
                data-testid="logInButton"
                size="lg"
                fontSize="font-size-subtitle"
                className={L.Button}
            />
        </WHookForm>
    );
}
